import {
  ConversationSimulatorConfiguration,
  ReportColumn,
  ColumnType,
} from "@/types/evaluate";
import { ReactNode, useMemo, useState } from "react";
import ChooseEngineStep from "../PromptTemplateType/ChooseEngineStep";
import ChooseTemplateStep from "../PromptTemplateType/ChooseTemplateStep";
import HomeStep from "./HomeStep";

interface ConversationSimulatorColumnBuilderProps {
  columnData: Partial<ReportColumn>;
  patchColumnData: (
    data:
      | Partial<ReportColumn>
      | ((prevState: Partial<ReportColumn>) => Partial<ReportColumn>),
  ) => void;
  availableColumns: ReportColumn[];
  navigatePrevious: () => void;
  saveColumnAction: (newColumnDataArg: ReportColumn) => void;
  editable: boolean;
  isLoading?: boolean;
}

const ConversationSimulatorColumnBuilder = ({
  columnData,
  patchColumnData,
  availableColumns,
  navigatePrevious,
  saveColumnAction,
  editable,
  isLoading = false,
}: ConversationSimulatorColumnBuilderProps) => {
  const [currentNavigation, setCurrentNavigation] = useState("home");

  const configuration: Partial<ConversationSimulatorConfiguration> =
    useMemo(() => {
      return columnData.configuration || {};
    }, [columnData]);

  const setConfiguration = (
    data:
      | Partial<ConversationSimulatorConfiguration>
      | ((
          prevState: Partial<ConversationSimulatorConfiguration>,
        ) => Partial<ConversationSimulatorConfiguration>),
  ) => {
    if (editable) {
      if (typeof data === "function") {
        patchColumnData((prevState) => {
          return {
            ...prevState,
            configuration: data(prevState?.configuration || {}),
          };
        });
      } else {
        patchColumnData({ configuration: data });
      }
    }
  };

  const saveSimulatorAction = () => {
    const newColumnData = {
      ...columnData,
      column_type: ColumnType.CONVERSATION_SIMULATOR,
      configuration: {
        template: configuration.template,
        engine: configuration.engine,
        prompt_template_variable_mappings:
          configuration.prompt_template_variable_mappings || {},
        ...(configuration.user_persona_source
          ? { user_persona_source: configuration.user_persona_source }
          : { user_persona: configuration.user_persona || "" }),
        is_user_first: configuration.is_user_first ?? false,
        conversation_samples: configuration.conversation_samples || [],
      },
    };

    saveColumnAction(newColumnData as ReportColumn);
  };

  const builderNavigation: { [key: string]: () => ReactNode } = {
    home: () => (
      <HomeStep
        navigateAway={navigatePrevious}
        navigateNext={saveSimulatorAction}
        setCurrentNavigation={setCurrentNavigation}
        availableColumns={availableColumns}
        configuration={configuration}
        setConfiguration={setConfiguration}
        columnName={columnData.name || ""}
        setColumnName={(name: string) => patchColumnData({ name })}
        editable={editable}
        columnId={columnData.id}
        isLoading={isLoading}
      />
    ),
    "select-prompt-template": () => (
      <ChooseTemplateStep
        navigateAway={() => {
          const templateName = configuration.template?.name;
          if (templateName && !columnData.name) {
            patchColumnData({ name: `simulator-${templateName}` });
          }
          setCurrentNavigation("home");
        }}
        configuration={configuration}
        setConfiguration={setConfiguration}
        editable={editable}
      />
    ),
    "select-engine-and-params": () => (
      <ChooseEngineStep
        navigateAway={() => setCurrentNavigation("home")}
        configuration={configuration}
        setConfiguration={setConfiguration}
        editable={editable}
      />
    ),
  };

  return builderNavigation[currentNavigation]();
};

export default ConversationSimulatorColumnBuilder;
